/**
 * NOLAS 2026, © Mika (Mikaël FOURNY). Fork TNE.
 * src/components/content/ContentBlock.tsx
 *
 * Rôle :
 *   LOT-H (M7/H) : Composant PUR qui affiche un bloc de contenu éditorial :
 *   la valeur configurée au back-office (clé `content.*` via useSiteContent)
 *   OU, à défaut (clé absente / vide / réseau down), le repli sur la clé
 *   i18n existante. Garantit « jamais de page vide » + zéro régression si le
 *   contenu n'est pas (encore) configuré.
 *
 *   Aucune dépendance à un store global ; ne lit que useSiteContent (query
 *   publique partagée) + react-intl. Réutilisable tel quel.
 *
 * Exemple :
 *   <ContentBlock contentKey="content.home.tagline" fallbackId="home.tagline" />
 *   <ContentBlock contentKey="content.about.intro"  fallbackId="about.intro"
 *                 multiline />
 *
 * Voir : hooks/useSiteContent.ts, api/admin/site-content.php.
 *
 * @author Mika
 * @since  2026-06-16 (LOT-H, contenu éditorial configurable)
 */

import { FormattedMessage } from 'react-intl';
import { useSiteContent } from '@/hooks/useSiteContent';
import styles from './ContentBlock.module.css';

export interface ContentBlockProps {
  /** Clé de contenu back-office, ex. 'content.about.intro'. */
  readonly contentKey: string;
  /** Clé i18n de repli si le contenu n'est pas configuré. */
  readonly fallbackId: string;
  /**
   * Valeurs passées à `<FormattedMessage>` UNIQUEMENT pour le repli i18n
   * (la valeur back-office est du texte brut, sans interpolation ICU).
   */
  readonly values?: Record<string, string | number>;
  /**
   * Préserve les retours à la ligne de la valeur configurée
   * (white-space: pre-line). Utile pour les textes longs (À propos).
   */
  readonly multiline?: boolean;
}

/**
 * Affiche la valeur configurée OU le repli i18n. Rend un fragment minimal
 * (le wrapper sémantique reste à la charge de la page hôte) ; en mode
 * `multiline`, enveloppe la valeur configurée dans un <span> pre-line.
 */
export function ContentBlock({
  contentKey,
  fallbackId,
  values,
  multiline = false,
}: ContentBlockProps): JSX.Element {
  const { get } = useSiteContent();
  const configured = get(contentKey);

  if (configured !== null) {
    return multiline ? (
      <span className={styles.multiline}>{configured}</span>
    ) : (
      <>{configured}</>
    );
  }

  // Repli : clé i18n existante (jamais de page vide).
  return <FormattedMessage id={fallbackId} values={values} />;
}

export default ContentBlock;
